import { Card, Grid, Icon, IconButton, Tooltip, InputLabel, FormControl, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { post, postNoResponse } from "utils/WebService";
import RegrasFilaCreateEdit from "./RegrasFilaCreateEdit";
import MDSnackbar from "components/MDSnackbar";
import MDAuditModal from "components/MDAuditModal";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

export default function RegrasFilaConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        nome: '',
        ativo: 'todos',
    });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [sbMessage, setSbMessage] = useState("");

    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [loadingAudit, setLoadingAudit] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditTitle, setAuditTitle] = useState("");

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };
        tableState.current = updatedTableState;

        import("utils/WebService").then(({ get }) => {
            get("/regra-fila/list-all").then((res) => {
                let list = res || [];
                const nomeFilter = updatedTableState.filtered.find(f => f.id === 'nome');
                const ativoFilter = updatedTableState.filtered.find(f => f.id === 'ativo');

                if (nomeFilter && nomeFilter.value) {
                    list = list.filter(item => item.nome && item.nome.toLowerCase().includes(nomeFilter.value.toLowerCase()));
                }
                if (ativoFilter && ativoFilter.value !== 'todos') {
                    const isAtivoBool = ativoFilter.value === 'ativo';
                    list = list.filter(item => (item.ativo !== undefined ? item.ativo : true) === isAtivoBool);
                }

                setData(list);
                setRecordsTotal(list.length);
                setPages(1);
                setLoadingTable(false);
                setAplicar(false);
            }).catch(() => {
                setLoadingTable(false);
                setAplicar(false);
            });
        });
    }, []);

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const backFunction = () => {
        setIsCreateEdit(false);
        setSelectedId(null);
        setAplicar(true);
        fetchData({ ...tableState.current });
    };

    const handleDeleteClick = (row) => {
        setItemToDelete(row);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (!itemToDelete) return;
        const targetId = itemToDelete.id || itemToDelete.cvId;
        postNoResponse(`/regra-fila/delete/${targetId}`)
            .then(() => {
                setSbMessage("Regra excluída com sucesso!");
                setSuccessSB(true);
                fetchData({ ...tableState });
            })
            .catch(() => {
                setSbMessage("Erro ao excluir regra. Verifique se existem vínculos.");
                setErrorSB(true);
            })
            .finally(() => {
                setDeleteDialogOpen(false);
                setItemToDelete(null);
            });
    };

    const handleOpenAuditoriaRow = (row) => {
        const id = row.id || row.cvId;
        setAuditTitle(`Auditoria da Regra: ${row.nome || ("ID " + id)}`);
        setAuditModalOpen(true);
        setLoadingAudit(true);
        import("utils/WebService").then(({ get }) => {
            get(`/regra-fila/${id}/historico`)
                .then(res => setAuditLogs(Array.isArray(res) ? res : []))
                .catch(err => {
                    console.error("Erro ao carregar auditoria:", err);
                    setAuditLogs([]);
                })
                .finally(() => setLoadingAudit(false));
        });
    };

    const handleOpenAuditoriaGeral = () => {
        setAuditTitle("Histórico de Auditoria - Todas as Regras da Fila");
        setAuditModalOpen(true);
        setLoadingAudit(true);
        import("utils/WebService").then(({ get }) => {
            get("/regra-fila/historico-geral")
                .then(res => setAuditLogs(Array.isArray(res) ? res : []))
                .catch(err => {
                    console.error("Erro ao carregar auditoria geral:", err);
                    setAuditLogs([]);
                })
                .finally(() => setLoadingAudit(false));
        });
    };

    const handleApplyFilter = () => {
        let filtered = [];
        if (filters.nome) {
            filtered.push({ id: 'nome', value: filters.nome });
        }
        if (filters.ativo && filters.ativo !== 'todos') {
            filtered.push({ id: 'ativo', value: filters.ativo });
        }
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
    };

    const handleCleanFilter = () => {
        setFilters({ nome: '', ativo: 'todos' });
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [] });
    };

    const columns = [
        {
            header: "ID",
            accessorKey: "id",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
        },
        {
            header: "Nome da Regra",
            accessorKey: "nome",
            cell: info => <DefaultCell>{info.getValue()}</DefaultCell>,
        },
        {
            header: "Equipe",
            accessorKey: "equipeNome",
            cell: info => <DefaultCell>{info.getValue() || "Todas / Geral"}</DefaultCell>,
        },
        {
            header: "Status",
            accessorKey: "ativo",
            cell: info => (
                <DefaultCell>
                    {info.getValue() ? "Ativo" : "Inativo"}
                </DefaultCell>
            ),
        },
        {
            header: "Ações",
            isSorted: false,
            cell: info => {
                const rowObj = info?.row?.original || info?.row || info;
                return (
                    <MDBox display="flex">
                        <Tooltip title="Histórico de Auditoria">
                            <IconButton onClick={() => handleOpenAuditoriaRow(rowObj)}>
                                <MDTypography variant="h4">
                                    <Icon color="info" sx={{ marginRight: 1 }}>history</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                            <IconButton
                                onClick={() => {
                                    setItemToEdit(rowObj);
                                    setEditDialogOpen(true);
                                }}>
                                <MDTypography variant="h4">
                                    <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                            <IconButton onClick={() => handleDeleteClick(info.row.original)}>
                                <MDTypography variant="h4">
                                    <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
                                </MDTypography>
                            </IconButton>
                        </Tooltip>
                    </MDBox>
                );
            },
            width: "18%"
        },
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <RegrasFilaCreateEdit recordId={selectedId} closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#4B4F5C" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Nome da Regra</InputLabel>
                            <MDInput size="small" placeholder="Nome..." value={filters.nome} onChange={({ target }) => setFilters({ ...filters, nome: target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2, color: darkMode ? "#ffffff" : "inherit" }}>Status</InputLabel>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={filters.ativo || "todos"}
                                    onChange={(e) => setFilters({ ...filters, ativo: e.target.value })}
                                    displayEmpty
                                    sx={{
                                        height: "40px",
                                        backgroundColor: darkMode ? "#3A3E4A" : "#ffffff",
                                        color: darkMode ? "#ffffff !important" : "inherit",
                                        "& .MuiSelect-select": { color: darkMode ? "#ffffff !important" : "inherit" },
                                        "& .MuiSvgIcon-root": { color: darkMode ? "#ffffff !important" : "inherit" }
                                    }}
                                >
                                    <MenuItem value="todos">Todos</MenuItem>
                                    <MenuItem value="ativo">Ativo</MenuItem>
                                    <MenuItem value="inativo">Inativo</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                            <MDBox display="flex" gap={1}>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="info" 
                                    onClick={() => { setSelectedId(null); setIsCreateEdit(true); }}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important"
                                    }}
                                >
                                    Adicionar
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="outlined" 
                                    color="info" 
                                    onClick={handleOpenAuditoriaGeral}
                                >
                                    <Icon sx={{ mr: 0.5 }}>history</Icon> Auditoria Geral
                                </MDButton>
                            </MDBox>
                            <MDBox display="flex" gap={1}>
                                <MDButton 
                                    size="medium" 
                                    variant="outlined" 
                                    color="secondary" 
                                    onClick={handleCleanFilter}
                                    sx={{
                                        color: darkMode ? "#ffffff !important" : undefined,
                                        borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                                    }}
                                >
                                    Limpar
                                </MDButton>
                                <MDButton 
                                    size="medium" 
                                    variant="contained" 
                                    color="info" 
                                    onClick={handleApplyFilter}
                                    sx={{
                                        backgroundColor: darkMode ? "#007bff !important" : undefined,
                                        color: "#ffffff !important"
                                    }}
                                >
                                    Aplicar
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={columns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    changeFilter={aplicar}
                    tableState={tableState}
                />
            </Grid>

            <Dialog 
                open={editDialogOpen} 
                onClose={() => { setEditDialogOpen(false); setItemToEdit(null); }}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#424550" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>Confirmar Edição</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        Deseja editar a regra existente <strong>{itemToEdit?.nome}</strong>?
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton 
                        onClick={() => { setEditDialogOpen(false); setItemToEdit(null); }} 
                        variant="outlined" 
                        color="secondary"
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton 
                        onClick={() => {
                            const rowObj = itemToEdit;
                            setEditDialogOpen(false);
                            setItemToEdit(null);
                            setSelectedId(rowObj);
                            setIsCreateEdit(true);
                        }} 
                        variant="contained" 
                        color="info"
                        sx={{
                            backgroundColor: darkMode ? "#007bff !important" : undefined,
                            color: "#ffffff !important",
                            fontWeight: "bold"
                        }}
                    >
                        Confirmar
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: darkMode ? "#424550" : "#ffffff",
                        color: darkMode ? "#ffffff" : "inherit"
                    }
                }}
            >
                <DialogTitle sx={{ color: darkMode ? "#ffffff" : "inherit" }}>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2" color={darkMode ? "white" : "text"}>
                        Tem certeza que deseja excluir a regra <strong>{itemToDelete?.nome}</strong>?
                    </MDTypography>
                    <br/>
                    <MDTypography variant="caption" color="error">
                        Atenção: Esta ação não poderá ser desfeita.
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton 
                        onClick={() => setDeleteDialogOpen(false)} 
                        variant="outlined" 
                        color="secondary"
                        sx={{
                            color: darkMode ? "#ffffff !important" : undefined,
                            borderColor: darkMode ? "rgba(255, 255, 255, 0.4) !important" : undefined
                        }}
                    >
                        Cancelar
                    </MDButton>
                    <MDButton onClick={confirmDelete} color="error" variant="contained">
                        Excluir
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* MODAL DE HISTÓRICO DE AUDITORIA */}
            <MDAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                title={auditTitle}
                loading={loadingAudit}
                logs={auditLogs}
                entityLabel="Regra da Fila"
            />

            <MDSnackbar
                color={successSB ? "success" : "error"}
                icon={successSB ? "check" : "warning"}
                title={successSB ? "Sucesso" : "Erro"}
                content={sbMessage}
                dateTime=""
                open={successSB || errorSB}
                onClose={() => { setSuccessSB(false); setErrorSB(false); }}
                close={() => { setSuccessSB(false); setErrorSB(false); }}
                bgWhite
            />
        </Grid>
    );
}
